<template>
    <div class="range">
        <template v-if="!preview">
            <input
                type="text"
                :min="min"
                :max="max"
                :name="name"
                :step="step"
                v-model="value_"
                v-bind="$attrs"
                @input="validateRange($event)"
                @blur="validateOnBlur()"
            />
            <span class="increase-decrease">
                <button type="button" @click="vary(true)" tabindex="-1"></button>
                <button type="button" @click="vary(false)" tabindex="-1"></button>
            </span>
        </template>
        <span v-else class="input-preview">
            {{ value_ }}
        </span>
    </div>
</template>
<script>
    export default {
        name: "NumberWidget",
        props: {
            name: String,
            min: Number,
            max: Number,
            step: {
                type: Number,
                default: 1,
            },
            value: undefined, // Can be String or Number
            preview: Boolean,
            isIntegerOnly: {
                type: Boolean,
                required: false,
                default: false,
            },
        },
        data() {
            return {
                error: "",
                warning: "",
                value_: isNaN(this.parse(this.value)) ? undefined : this.parse(this.value),
            };
        },
        computed: {
            stepDecimals() {
                return this.step % 1 ? this.step.toString().split(".")[1].length : 0;
            },
        },
        methods: {
            setError(error) {
                this.error = error;
                this.$emit("error", error);
            },
            setWarning(warning) {
                this.warning = warning;
                this.$emit("warning", warning);
            },
            clearError(error) {
                if (this.error === error) {
                    this.error = "";
                    this.$emit("error", "");
                }
            },
            clearWarning(warning) {
                if (this.warning === warning) {
                    this.warning = "";
                    this.$emit("warning", "");
                }
            },
            clearAllIssues() {
                this.warning = "";
                this.error = "";
                this.$emit("warning", "");
                this.$emit("error", "");
            },
            validateOnBlur() {
                if (this.$attrs.required && !this.value_ && this.value_ !== 0) {
                    this.setError("Ange värde");

                    return;
                }

                this.validateMinMax({ level: "error" });
            },
            validateMinMax({ level = "error" }) {
                const message = `${!isNaN(this.min) ? `Min: ${this.min}` : ""} ${
                    !isNaN(this.max) ? `Max: ${this.max}` : ""
                }`;

                if ((!isNaN(this.min) && this.value_ < this.min) || (!isNaN(this.max) && this.value_ > this.max)) {
                    this.clearWarning(message);
                    this.clearError(message);

                    if (level === "error") {
                        this.setError(message);
                    } else {
                        this.setWarning(message);
                    }

                    return false;
                }

                this.clearWarning(message);
                this.clearError(message);

                return true;
            },
            vary(positive) {
                let numberValue = this.parse(this.value_?.toString().replace(",", "."));

                if (this.value_ === "" || isNaN(numberValue)) {
                    numberValue = 0;
                }

                numberValue = positive ? numberValue + this.step : numberValue - this.step;

                this.value_ = numberValue.toFixed(this.stepDecimals);
                this.validateRange();
            },
            validateRange(inputEvent) {
                const numberValue = this.parse(this.value_);

                if (inputEvent?.target.value === "") {
                    if (this.$attrs.required) {
                        this.setError("Måste ha ett värde");
                    } else {
                        this.clearAllIssues();
                    }

                    this.$emit("input", this.value_);

                    return true;
                }

                if (!/^[0-9]+([,.]?[0-9]?)?$/.test(this.value_)) {
                    this.setError("Endast siffror");
                    this.$emit("input", this.value_);

                    return true;
                }

                const modulus = this.step % 1;

                if (modulus === 0 && numberValue % 1 > 0) {
                    this.setError("Decimaler är ej tillåtna");
                    this.$emit("input", this.value_);

                    return true;
                }

                // Resettar error/warning
                this.clearAllIssues();

                this.validateMinMax({ level: "warning" });

                this.$emit("input", numberValue);

                // Ändra till decimal för visuellt syfte
                this.value_ = this.value_.toString().replace(".", ",");
            },

            parse(value) {
                // Ändra till punkt för att behandlas som en giltig siffra
                const modifiedValue = value?.toString().replace(",", ".");

                return this.isIntegerOnly ? parseInt(modifiedValue) : parseFloat(modifiedValue);
            },
        },
        watch: {
            min() {
                this.validateRange();
            },
            max() {
                this.validateRange();
            },
            value() {
                // Ändra till decimal för visuellt syfte
                this.value_ = this.value?.toString().replace(".", ",");
            },
        },
    };
</script>

<style lang="sass" scoped>
    @import "@/style/variables"

    /* Bootstrap - Start */
    @import "bootstrap/scss/functions"
    @import "bootstrap/scss/variables"
    @import "bootstrap/scss/mixins"
    @import "bootstrap/scss/root"
    @import "bootstrap/scss/reboot"

    @import "bootstrap/scss/type"
    @import "bootstrap/scss/forms"
    @import "bootstrap/scss/buttons"
    @import "bootstrap/scss/button-group"
    @import "bootstrap/scss/input-group"
    @import "bootstrap/scss/custom-forms"
    /* Bootstrap - End */

    @import "@/style/deprecated_main"

    .range
        display: flex

        input
            border-top-right-radius: 0 !important
            border-bottom-right-radius: 0 !important
            border-right: none

        .increase-decrease
            display: flex
            flex-direction: column
            background-image: url('@/assets/plus_minus.svg')
            border: 1px solid #95A1B1
            border-left: none
            border-bottom-right-radius: 4px !important
            border-top-right-radius: 4px !important
            background-color: #FFFFFF

        button
            opacity: 0
            height: 22px
            width: 22px
            flex: 0 0 auto
            border: none

    //Supposed to only target IE10, but unfortunately targets IE11 as well.
    @media screen and (min-width: 0 / 0)
        .range
            margin-right: 22px

    // Hide spinners in Firefox
    input[type=number]
        -moz-appearance: textfield

    // Hide spinners in other browsers
    input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button
        -webkit-appearance: none
        margin: 0
</style>
